import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import DocsSidebar from '@/components/dashboard/DocsSidebar';
import DashboardNav from '@/components/nav/DashboardNav';
import { Toaster } from "@/components/ui/sonner"
import usePurchase from '@/hooks/usePurchase';
import Loader from '../Loader'
import { X } from 'lucide-react';
import useTW from './useTW';

interface DashboardLayoutProps {
  openSearchModal: () => void;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ openSearchModal }) => {
  const location = useLocation();
  const { token } = useAuth();
  const isStylesLoaded = useTW();
  const purchase = usePurchase();
  const [usePadding, setUsePadding] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);


  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    if (token) {
      purchase.actionPurchaseIntent();
    }
  }, [purchase, token]);

  useEffect(() => {
    if(location.pathname.includes('/playground')){
      setUsePadding(false)
    }else{
      setUsePadding(true)
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/dashboard')) {
      const container = document.getElementById('docs-scroll-container');
      if (container) {
        container.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    }
  }, [location.pathname]);


  const showDocsSidebar = location.pathname.includes('/docs');
  const showDashboardSidebar = location.pathname.includes('/dashboard');
  const showSidebar = showDocsSidebar || showDashboardSidebar;


  if (!isStylesLoaded) {
    return ( <Loader />);
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-gray-50">
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-[rgba(0,0,0,0.2)] backdrop-blur-xs z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      {showSidebar && (
        <div className={`
          fixed lg:sticky top-0 h-dvh z-50 lg:z-auto
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Close button for mobile */}
          <button
            onClick={closeSidebar}
            className="lg:hidden cursor-pointer absolute top-4 right-4 z-10 p-2 bg-white rounded-md"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>

          {showDocsSidebar && <DocsSidebar openSearchModal={openSearchModal} onLinkClick={closeSidebar} />}
          {showDashboardSidebar && <DashboardSidebar openSearchModal={openSearchModal} onLinkClick={closeSidebar} />}
        </div>
      )}

      <Toaster />

      <div className="flex flex-col flex-1 pt-6 min-h-0">
        <DashboardNav onMenuClick={toggleSidebar} />
        <main
          id="docs-scroll-container"
          className={`
            flex-1 space-y-6 overflow-y-auto
            ${usePadding ? 'p-4 sm:p-6 lg:p-8 lg:pt-0' : 'p-0'}
          `}
        >
            <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
